import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CalculatorFormField,
  CalculatorFormOption,
  CalculatorFormSchema,
  CalculatorVisibilityRule,
  isBeneficiaryField,
} from '../../../../services/vita-calculator.service';
import {
  VitaBeneficiario,
  VitaBeneficiarioCreateRequest,
  VitaBeneficiarioService,
  VitaRoutingValidationResult,
} from '../../../../services/vita-beneficiario.service';
import { sinTildes } from '../../../../../shared/utils/texto-sin-tildes.util';

/**
 * Mapeo entre keys del schema de Vita y columnas fijas del DTO/entity.
 * Todo lo que no esté acá cae en `datosBancarios` (JSON libre).
 */
const IDENTITY_MAP: Record<string, string> = {
  beneficiary_first_name: 'nombre',
  beneficiary_last_name: 'apellidos',
  beneficiary_email: 'email',
  beneficiary_document_type: 'tipoId',
  beneficiary_document_number: 'numeroId',
  phone: 'telefono',
};

/**
 * Documentos de persona JURÍDICA. Espeja `vita-beneficiary-type.util.ts` del
 * backend (NIT en Colombia, RUC en Perú): el payout deriva `beneficiary_type`
 * del documento guardado con esta misma regla, así que las dos puntas tienen
 * que coincidir. Si divergen, el alta valida OK y el envío rebota.
 */
const CORPORATE_DOC_TYPES = ['NIT', 'RUC'];

function isCorporateDoc(tipoId: any): boolean {
  return CORPORATE_DOC_TYPES.includes(
    String(tipoId ?? '')
      .trim()
      .toUpperCase(),
  );
}

/**
 * Busca la opción "Persona Natural" en la lista de options de un select,
 * probando los valores más comunes que publica Vita (varía por variante):
 * "Individual" / "individual" / "natural".
 * Si no hay match, cae a la primera opción como fallback seguro.
 */
function pickNaturalPersonOption(
  options: Array<{ value: string | number; label: string }> | undefined,
): string | number | null {
  if (!options || options.length === 0) return null;
  const preferred = ['Individual', 'individual', 'natural'];
  for (const p of preferred) {
    const found = options.find((o) => String(o.value) === p);
    if (found) return found.value;
  }
  return options[0].value;
}

/**
 * Espejo del anterior para persona JURÍDICA. A diferencia de aquél NO cae a la
 * primera option: si Vita no publica un token de empresa reconocible en esta
 * variante, devuelve null y el llamador vuelve a persona natural — que es el
 * comportamiento histórico.
 */
function pickCorporateOption(
  options: Array<{ value: string | number; label: string }> | undefined,
): string | number | null {
  if (!options || options.length === 0) return null;
  for (const p of ['Corporate', 'corporate', 'business']) {
    const found = options.find((o) => String(o.value) === p);
    if (found) return found.value;
  }
  return null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface AddVitaBeneficiarioDialogData {
  countryCode: string;
  variant: string;
  variantLabel: string;
  schema: CalculatorFormSchema;
  /** Modo edición: id del beneficiario + valores actuales para pre-cargar. */
  editId?: string;
  initialValues?: Record<string, any>;
}

@Component({
  selector: 'app-add-vita-beneficiario-dialog',
  templateUrl: './add-vita-beneficiario-dialog.component.html',
  styleUrls: ['./add-vita-beneficiario-dialog.component.sass'],
})
export class AddVitaBeneficiarioDialogComponent implements OnInit, OnDestroy {
  formValues: Record<string, any> = {};
  formErrors: Record<string, string> = {};
  saving = false;
  serverError: string | null = null;

  // ─── Validación on-blur contra Vita (Capa 2) ───
  // Aplica para países con `validate_beneficiary` disponible (CO/BR/MX/EU/GB/US/AR).
  // Para VE queda en null (Vita no expone el endpoint); ahí confiamos en la
  // validación de prefijo `transferswap_code` de Capa 1.
  routingValidation: VitaRoutingValidationResult | null = null;
  routingValidating = false;
  private routingDebounceTimer: any = null;
  private readonly ROUTING_DEBOUNCE_MS = 700;

  /**
   * Fields a renderizar — se calculan UNA VEZ en ngOnInit y se cachean.
   * Si se usara un getter, Angular devuelve un array nuevo en cada change
   * detection y el `*ngFor` re-renderiza constantemente, freezando la página.
   */
  effectiveFields: CalculatorFormField[] = [];

  constructor(
    public dialogRef: MatDialogRef<
      AddVitaBeneficiarioDialogComponent,
      VitaBeneficiario | null
    >,
    @Inject(MAT_DIALOG_DATA)
    public data: AddVitaBeneficiarioDialogData,
    private svc: VitaBeneficiarioService,
  ) {}

  ngOnInit(): void {
    // Modo edición: pre-cargar valores actuales del beneficiario.
    if (this.data.initialValues) {
      for (const [k, v] of Object.entries(this.data.initialValues)) {
        if (v !== undefined && v !== null) this.formValues[k] = v;
      }
    }

    // `beneficiary_type` se DERIVA del documento (ver `syncBeneficiaryType`) y
    // se resuelve acá para que los fields condicionados a él queden bien desde
    // el vamos. Va DESPUÉS de `initialValues` porque en edición el tipo de
    // documento viene de ahí. NO se envía al backend: `isBeneficiaryField` lo
    // filtra en `buildCreateRequest` y el backend lo deriva del documento.
    this.syncBeneficiaryType();

    this.effectiveFields = this.computeEffectiveFields();
    for (const f of this.effectiveFields) {
      if (this.formValues[f.key] === undefined) {
        this.formValues[f.key] = '';
      }
    }
  }

  ngOnDestroy(): void {
    if (this.routingDebounceTimer) {
      clearTimeout(this.routingDebounceTimer);
      this.routingDebounceTimer = null;
    }
  }

  /**
   * true si este beneficiario es una EMPRESA para esta variante. Exige las dos
   * cosas: que el documento elegido sea de persona jurídica (NIT/RUC) y que
   * Vita publique `beneficiary_type` + `company_name` en la variante. Si la
   * variante no trae esos fields, se comporta como antes (persona natural),
   * igual que el gate de completitud del backend — que también es
   * schema-driven. Así las dos puntas piden lo mismo, siempre.
   */
  private esBeneficiarioEmpresa(): boolean {
    const fields = this.data.schema?.fields || [];
    const tieneTipo = fields.some((f) => f.key === 'beneficiary_type');
    const tieneRazonSocial = fields.some((f) => f.key === 'company_name');
    if (!tieneTipo || !tieneRazonSocial) return false;
    return isCorporateDoc(this.formValues['beneficiary_document_type']);
  }

  /**
   * `beneficiary_type` no se le pregunta al usuario (está filtrado de la lista
   * visible): se DERIVA del tipo de documento, igual que en el backend. Con
   * NIT/RUC pasa a `Corporate`, y eso hace aparecer `company_name` (razón
   * social, `when: Corporate`) y esconder nombre/apellido (`when: Individual`)
   * — Vita EXIGE la razón social en ese caso (304 "Parameter company_name not
   * found" si falta, con la plata del cliente ya debitada).
   *
   * Si Vita no publica `beneficiary_type` en la variante no hay nada que
   * derivar. Si publica el field pero no el token esperado, cae al
   * comportamiento previo (persona natural) para no dejar el form colgado.
   */
  private syncBeneficiaryType(): void {
    const btField = (this.data.schema?.fields || []).find(
      (f) => f.key === 'beneficiary_type',
    );
    if (!btField) return;
    const natural = pickNaturalPersonOption(btField.options);
    const valor = this.esBeneficiarioEmpresa()
      ? pickCorporateOption(btField.options) ?? natural
      : natural;
    if (valor !== null && valor !== undefined) {
      this.formValues['beneficiary_type'] = valor;
    }
  }

  /**
   * Construye la lista de fields:
   *  - Los que Vita publica para esta variante (filtrando fc_ y purpose).
   *  - Si Vita NO pide email (caso COBREB, por ejemplo), inyectamos uno
   *    sintético al principio. Si SÍ lo pide, usamos el que ya trae para no
   *    duplicar inputs con el mismo `key`.
   */
  private computeEffectiveFields(): CalculatorFormField[] {
    const fromVita = (this.data.schema?.fields || []).filter((f) =>
      isBeneficiaryField(f.key),
    );
    const hasEmail = fromVita.some((f) => f.key === 'beneficiary_email');
    if (hasEmail) return fromVita;
    const synth: CalculatorFormField = {
      key: 'beneficiary_email',
      name: 'Email del beneficiario',
      type: 'email',
      min: 5,
      max: 120,
    };
    // Email primero (buena UX — lo ponemos arriba).
    return [synth, ...fromVita];
  }

  get visibleFields(): CalculatorFormField[] {
    // Visibilidad depende de `formValues` (reglas `when`), así que este sí
    // tiene que recalcularse, pero el costo es O(N) chico sobre un array ya
    // filtrado.
    return this.effectiveFields.filter((f) => this.isFieldVisible(f));
  }

  /**
   * Evalúa una regla de visibilidad de Vita. `value` puede ser string o
   * array — si es array, el match es "value ∈ lista" (caso típico: un banco
   * que aplica tanto a Pago Móvil como a Transferencia bancaria).
   */
  private matchesRule(
    rule: CalculatorVisibilityRule | undefined,
    values: Record<string, any>,
  ): boolean {
    if (!rule) return true;
    const current = String(values?.[rule.key] ?? '');
    const expected = Array.isArray(rule.value) ? rule.value : [rule.value];
    return expected.some((v) => String(v) === current);
  }

  /** Una option está visible si cumple su propia regla `visible` (si la trae). */
  isOptionVisible(opt: CalculatorFormOption): boolean {
    return this.matchesRule(opt.visible, this.formValues);
  }

  /**
   * Regla de visibilidad del field:
   *  1) Si las options traen `visible`, esa es la fuente de verdad — el
   *     field aparece si hay al menos una option visible. (Vita a veces
   *     deja `when` a nivel field desactualizado; ej. VEVES `bank_code`
   *     tiene when=Pago movil pero options aplican a ambas transferencias).
   *  2) Si no, cae al `when` del field.
   */
  isFieldVisible(field: CalculatorFormField): boolean {
    const hasOptionVisibility =
      field.type === 'select' &&
      !!field.options &&
      field.options.some((o) => !!o.visible);
    if (hasOptionVisibility) {
      return (field.options || []).some((o) => this.isOptionVisible(o));
    }
    return this.matchesRule(field.when, this.formValues);
  }

  /** Options filtradas por su propia `visible` — feed del `*ngFor` del select. */
  visibleOptions(field: CalculatorFormField): CalculatorFormOption[] {
    return (field.options || []).filter((o) => this.isOptionVisible(o));
  }

  /**
   * Si el usuario ya eligió un banco en algún select cuyas options traen
   * `transferswap_code`, devuelve ese código (y el label del banco para
   * el mensaje de error). Vita publica `transferswap_code` en países donde
   * los primeros N dígitos del número de cuenta son el código del banco
   * (ej. VE: cuenta de 20 dígitos, los primeros 4 = `transferswap_code`).
   * 100% data-driven: si mañana Vita lo agrega a otro país, arranca solo.
   */
  private resolveRoutingPrefix(): { code: string; bankLabel: string } | null {
    for (const f of this.effectiveFields) {
      if (f.type !== 'select' || !f.options) continue;
      const selected = this.formValues[f.key];
      if (selected === undefined || selected === null || selected === '') continue;
      const opt = f.options.find(
        (o) => String(o.value) === String(selected),
      );
      const code = opt?.transferswap_code;
      if (code) {
        return { code: String(code), bankLabel: opt?.label ?? '' };
      }
    }
    return null;
  }

  /**
   * Re-valida los fields de cuenta (`account_*` de longitud fija) cuando
   * cambia el select de banco — su validez depende del prefijo del banco
   * elegido, no solo de su propia longitud.
   */
  private revalidateRoutingFields(): void {
    for (const f of this.effectiveFields) {
      if (f.type !== 'text') continue;
      if (!f.key.startsWith('account')) continue;
      if (!f.min || f.min !== f.max) continue;
      const v = this.formValues[f.key];
      if (v === undefined || v === null || v === '') continue;
      const err = this.validateFieldValue(f);
      if (err) this.formErrors[f.key] = err;
      else delete this.formErrors[f.key];
    }
  }

  /**
   * Cuando el usuario cambia un field que controla la visibilidad de otras
   * options (ej. `transfer_type`), limpiamos selecciones que quedaron
   * "huérfanas" — la option ya no está visible en el select filtrado.
   * Evita enviar combinaciones inválidas sin que el usuario se entere.
   */
  private cleanupDependentSelections(): void {
    for (const f of this.effectiveFields) {
      if (f.type !== 'select' || !f.options) continue;
      const v = this.formValues[f.key];
      if (v === undefined || v === null || v === '') continue;
      const stillVisible = this.visibleOptions(f).some(
        (o) => String(o.value) === String(v),
      );
      if (!stillVisible) {
        this.formValues[f.key] = '';
        if (this.formErrors[f.key]) delete this.formErrors[f.key];
      }
    }
  }

  hintForField(field: CalculatorFormField): string | null {
    if (field.type === 'email') return 'Ej: nombre@dominio.com';
    if (field.type === 'text') {
      if (field.min && field.max) return `${field.min}–${field.max} caracteres`;
      if (field.min) return `Mínimo ${field.min} caracteres`;
      if (field.max) return `Máximo ${field.max} caracteres`;
    }
    return null;
  }

  /**
   * Sanitiza on-input:
   *  - Text → sin tildes + MAYÚSCULAS + quita espacios dobles / de inicio.
   *  - Email → solo trim de inicio (respetar case y puntos).
   *  - Select → lo que eligió el usuario, sin tocar.
   *
   * Las tildes se limpian acá para que el cliente VEA `JOSE` mientras escribe
   * y no se entere recién en el envío. El que garantiza que no se guarden es
   * `buildCreateRequest` — este input no lo ve todo (al editar, los valores
   * llegan precargados desde la base sin pasar por acá).
   */
  onFieldInput(field: CalculatorFormField, event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const raw = target.value ?? '';
    let next = raw;
    if (field.type === 'text') {
      next = sinTildes(raw)
        .toUpperCase()
        .replace(/^\s+/, '')
        .replace(/ {2,}/g, ' ');
    } else if (field.type === 'email') {
      next = raw.replace(/^\s+/, '');
    }
    if (next !== raw) {
      target.value = next;
    }
    this.formValues[field.key] = next;
    // Idem `onFieldBlur`: en las variantes donde el documento es un input de
    // texto (y no un select) el cambio llega por acá.
    if (field.key === 'beneficiary_document_type') {
      this.syncBeneficiaryType();
    }
    // Validación reactiva: limpiar error si ahora es válido.
    if (this.formErrors[field.key]) {
      const err = this.validateFieldValue(field);
      if (!err) delete this.formErrors[field.key];
    }
    // Si tocaron el field de routing (o un select que cambia qué banco
    // valida), agendar validación on-blur contra Vita con debounce.
    if (this.isRoutingField(field) || field.type === 'select') {
      this.scheduleRoutingValidation();
    }
  }

  onFieldBlur(field: CalculatorFormField): void {
    const raw = this.formValues[field.key];
    const v = typeof raw === 'string' ? raw.trim() : raw;
    if (typeof raw === 'string' && raw !== v) this.formValues[field.key] = v;
    // Al salir del campo, validamos todo — incluyendo "requerido".
    const err = this.validateFieldValue(field);
    if (err) this.formErrors[field.key] = err;
    else delete this.formErrors[field.key];
    // El tipo de documento decide si el beneficiario es persona o empresa
    // (NIT/RUC), y eso cambia qué fields pide Vita: razón social en vez de
    // nombre/apellido. Hay que re-derivarlo ANTES de limpiar dependientes.
    if (field.key === 'beneficiary_document_type') {
      this.syncBeneficiaryType();
    }
    // Si este field controla la visibilidad de otros (ej. transfer_type),
    // limpiar selecciones dependientes que quedaron huérfanas. Y si cambió
    // un select con transferswap_code, re-validar las cuentas de longitud
    // fija — su prefijo depende del banco elegido.
    if (field.type === 'select') {
      this.cleanupDependentSelections();
      this.revalidateRoutingFields();
    }
    // Capa 2: tras blur, agendar validación contra Vita (si este field es
    // el de routing, o si un select del form pudo cambiar contexto).
    if (this.isRoutingField(field) || field.type === 'select') {
      this.scheduleRoutingValidation();
    }
  }

  private validateFieldValue(field: CalculatorFormField): string | null {
    const raw = this.formValues[field.key];
    const v = typeof raw === 'string' ? raw.trim() : raw;
    if (typeof raw === 'string' && raw !== v) this.formValues[field.key] = v;
    if (v === undefined || v === null || v === '') return 'Campo requerido';
    if (
      (field.type === 'text' || field.type === 'email') &&
      typeof v === 'string'
    ) {
      if (field.min && v.length < field.min) return `Mínimo ${field.min} caracteres`;
      if (field.max && v.length > field.max) return `Máximo ${field.max} caracteres`;
    }
    if (field.type === 'email' && typeof v === 'string' && !EMAIL_RE.test(v)) {
      return 'Formato de email inválido';
    }
    if (field.type === 'select' && field.options) {
      // Validamos solo contra options visibles — una option oculta por
      // `visible` no cuenta aunque siga en `field.options`.
      const valid = this.visibleOptions(field).some(
        (o) => String(o.value) === String(v),
      );
      if (!valid) return 'Seleccione una opción válida';
    }
    // Validación cross-field data-driven: si alguna option seleccionada trae
    // `transferswap_code` (caso VE — Vita publica el código de routing en
    // cada banco), los fields de cuenta de longitud fija deben empezar con
    // ese código. Se limita a keys `account_*` para no confundirse con
    // teléfonos/alias/pix_key/clabe que también son text min==max.
    if (
      field.type === 'text' &&
      typeof v === 'string' &&
      field.min &&
      field.min === field.max &&
      field.key.startsWith('account')
    ) {
      const routing = this.resolveRoutingPrefix();
      if (
        routing &&
        field.min > routing.code.length &&
        !v.startsWith(routing.code)
      ) {
        return `El banco "${routing.bankLabel}" usa el código ${routing.code}. La cuenta debe comenzar con ${routing.code}.`;
      }
    }
    return null;
  }

  private validateForm(): boolean {
    this.formErrors = {};
    for (const f of this.visibleFields) {
      const err = this.validateFieldValue(f);
      if (err) this.formErrors[f.key] = err;
    }
    // Bloqueo en el front solo por errores de formato que Vita confirma sin
    // importar el ambiente (banco incorrecto 578 / formato inválido 579).
    // `not_registered` (exists:false) NO bloquea acá: en sandbox Vita no conoce
    // llaves reales; el backend lo enforce en PROD. NO escribimos formErrors
    // para no duplicar (el feedback inline de routing ya lo muestra).
    const routingBlocks =
      this.routingValidation?.status === 'bank_mismatch' ||
      this.routingValidation?.status === 'invalid_format';
    return Object.keys(this.formErrors).length === 0 && !routingBlocks;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    if (!this.validateForm()) {
      const firstErr = Object.keys(this.formErrors)[0];
      setTimeout(() => {
        document
          .getElementById('dlg-field-' + firstErr)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    const dto = this.buildCreateRequest();
    if (!dto) return;
    this.saving = true;
    this.serverError = null;
    const req$ = this.data.editId
      ? this.svc.editar(this.data.editId, dto)
      : this.svc.create(dto);
    req$.subscribe({
      next: (beneficiario) => {
        this.saving = false;
        this.dialogRef.close(beneficiario);
      },
      error: (err) => {
        this.saving = false;
        this.serverError =
          typeof err === 'string' ? err : 'No se pudo guardar el beneficiario.';
      },
    });
  }

  /**
   * Único punto por el que pasa TODO lo que se guarda — tipeado, pegado y
   * precargado al editar. Las tildes se limpian acá y no solo en el input
   * porque al editar un beneficiario viejo los valores vienen de la base
   * (`valoresIniciales`) y nunca tocan `onFieldInput`: sin esto, re-guardar
   * uno con tilde lo volvía a guardar con tilde.
   *
   * Se excluyen `email` (el acento lo ataja el regex de formato, y bajar un
   * dominio a ASCII lo rompería) y `select` (el value es un código de Vita:
   * tocarlo lo deja sin match contra sus propias options).
   */
  private limpiarParaGuardar(key: string, tipo: string | undefined): any {
    const value = this.formValues[key];
    if (typeof value !== 'string') return value;
    if (tipo === 'email' || tipo === 'select') return value;
    return sinTildes(value);
  }

  private buildCreateRequest(): VitaBeneficiarioCreateRequest | null {
    const identity: Record<string, any> = {};
    const datos: Record<string, any> = {};
    const esEmpresa = this.esBeneficiarioEmpresa();
    const tipoPorKey = new Map<string, string>(
      this.effectiveFields.map((f) => [f.key, f.type] as [string, string]),
    );
    for (const key of Object.keys(this.formValues)) {
      if (!isBeneficiaryField(key)) continue;
      // La razón social solo aplica a empresas. El field se esconde al volver
      // el documento a persona, pero su valor sigue en `formValues` (los text
      // no se limpian), así que acá no se arrastra.
      if (key === 'company_name' && !esEmpresa) continue;
      const col = IDENTITY_MAP[key];
      const value = this.limpiarParaGuardar(key, tipoPorKey.get(key));
      if (col) identity[col] = value;
      else if (value !== undefined && value !== null && value !== '') {
        datos[key] = value;
      }
    }
    const validateType = this.resolveValidateType();
    const validateValue = validateType
      ? this.resolveValidateValue(validateType)
      : undefined;

    // Empresa: Vita esconde nombre/apellido (`when: Individual`) y pide la
    // razón social. Pero `nombre` es NOT NULL en la tabla, el DTO del backend
    // lo exige y el payout manda `beneficiary_first_name` desde esa columna
    // siempre — así que la razón social hace de nombre y apellidos va vacío.
    // Sin esto, guardar una empresa devolvía 400 (`nombre should not be empty`).
    const razonSocial = String(datos['company_name'] ?? '').trim();
    const nombre =
      esEmpresa && razonSocial ? razonSocial : identity['nombre'] || '';

    return {
      paisCodigo: this.data.countryCode,
      variante: this.data.variant,
      varianteLabel: this.data.variantLabel,
      nombre,
      apellidos: esEmpresa ? undefined : identity['apellidos'] || undefined,
      email: identity['email'] || undefined,
      telefono: identity['telefono'] || undefined,
      tipoId: identity['tipoId'] || '',
      numeroId: identity['numeroId'] || '',
      datosBancarios: datos,
      bancoNombre: this.resolveBancoNombre(),
      cuentaMask: this.resolveCuentaMask(),
      validateType,
      validateValue,
    };
  }

  // ─── Capa 2: validación on-blur contra Vita ───

  /**
   * El field donde vive el value que se valida contra Vita (routing real).
   * Es el primero no-vacío entre los identificadores universales de routing.
   * Null si la variante no tiene campo de routing validable.
   */
  private resolveValidateFieldKey(): string | null {
    const candidates = ['account_bank', 'pix_key', 'clabe', 'iban', 'cvu', 'alias'];
    for (const k of candidates) {
      const v = this.formValues[k];
      if (v != null && v !== '') return k;
    }
    return null;
  }

  /** True si este field es el que se valida contra Vita. Controla el render inline. */
  isRoutingField(field: CalculatorFormField): boolean {
    return this.resolveValidateFieldKey() === field.key;
  }

  /**
   * Agenda una validación contra Vita. Se llama en cada cambio del form;
   * el debounce evita pegarle a Vita por cada tecla. Si no hay validateType
   * para este país (caso VE) o el value aún es muy corto, limpia estado.
   */
  private scheduleRoutingValidation(): void {
    if (this.routingDebounceTimer) {
      clearTimeout(this.routingDebounceTimer);
      this.routingDebounceTimer = null;
    }
    this.routingDebounceTimer = setTimeout(() => {
      this.runRoutingValidation();
    }, this.ROUTING_DEBOUNCE_MS);
  }

  private runRoutingValidation(): void {
    const type = this.resolveValidateType();
    const value = type ? this.resolveValidateValue(type) : undefined;
    if (!type || !value) {
      this.routingValidation = null;
      this.routingValidating = false;
      return;
    }
    // Umbral chico para evitar llamar con strings parciales que Vita
    // rechazaría como 579 y darían un falso "formato inválido".
    if (value.length < 5) {
      this.routingValidation = null;
      this.routingValidating = false;
      return;
    }
    this.routingValidating = true;
    this.svc
      .validateRouting({
        paisCodigo: this.data.countryCode,
        validateType: type,
        validateValue: value,
        variante: this.data.variant,
        datosBancarios: this.datosDeRuteo(),
      })
      .subscribe({
        next: (res) => {
          this.routingValidation = res;
          this.routingValidating = false;
          // Reflejar error duro en el field correspondiente.
          // El bloqueo se evalúa en validateForm; acá NO tocamos formErrors
          // para no duplicar el mensaje (el feedback inline de routing lo
          // muestra). Limpiamos cualquier error viejo que haya quedado igual.
          const key = this.resolveValidateFieldKey();
          if (key && this.formErrors[key] === res.message) {
            delete this.formErrors[key];
          }
        },
        error: () => {
          this.routingValidation = null;
          this.routingValidating = false;
        },
      });
  }

  /**
   * Detecta si la variante soporta `validate_beneficiary` (BREB/PIX/CLABE/
   * IBAN/ABA/CVU). El único dato "per-country" que conoce Vita pero NO
   * publica en /withdrawal_rules. Lo inferimos buscando el sufijo en el
   * código de la variante cuando aparezca; para variantes "normales" (cocop,
   * clclp, brbrl...) caemos al map conocido por país.
   */
  private resolveValidateType(): string | undefined {
    const v = (this.data.variant || '').toLowerCase();
    if (v.includes('breb')) return 'breb';
    if (v.includes('pix')) return 'pix';
    if (v.includes('clabe')) return 'clabe';
    if (v.includes('iban')) return 'iban';
    if (v.includes('aba')) return 'aba';

    // Cuenta bancaria clásica: Vita NO sabe resolver su existencia. El viejo
    // fallback `CO → breb` le preguntaba por una cuenta de ahorros como si
    // fuera llave Bre-B, Vita respondía exists:false y en prod el backend
    // bloqueaba el alta de cuentas perfectamente válidas. Sin tipo no hay
    // chequeo de existencia (la validez se confirma por completitud/formato).
    if (this.esCuentaClasica()) return undefined;

    // El campo presente manda sobre el país: es el dato que Vita realmente
    // valida, y hay variantes que no traen el sufijo en su código.
    const porCampo: Record<string, string> = {
      pix_key: 'pix',
      clabe: 'clabe',
      iban: 'iban',
      cvu: 'cvu_alias',
      alias: 'cvu_alias',
    };
    const campo = this.resolveValidateFieldKey();
    if (campo && porCampo[campo]) return porCampo[campo];

    // Fallback por país, solo para los rieles que son 100% de llave. Colombia
    // ya NO está acá: tiene los dos rieles a la vez (`cobreb` y `co`).
    const byCountry: Record<string, string> = {
      BR: 'pix',
      MX: 'clabe',
      AR: 'cvu_alias',
      US: 'aba',
      EU: 'iban',
      GB: 'iban',
    };
    return byCountry[this.data.countryCode.toUpperCase()];
  }

  /**
   * Mismo criterio que el backend (`esCuentaClasica`): un riel de LLAVE nunca
   * pide banco ni tipo de cuenta — Vita los deduce de la llave. Se mira el
   * schema antes que lo tipeado, así el veredicto no cambia mientras el
   * usuario completa el form.
   */
  private esCuentaClasica(): boolean {
    const enSchema = new Set(this.effectiveFields.map((f) => f.key));
    const presente = (k: string): boolean => {
      if (enSchema.has(k)) return true;
      const x = this.formValues[k];
      return x != null && String(x).trim() !== '';
    };
    if (['pix_key', 'clabe', 'iban', 'cvu', 'alias'].some(presente)) {
      return false;
    }
    return ['bank_code', 'account_type_bank'].some(presente);
  }

  /**
   * Subset de datos que el backend mira para distinguir cuenta clásica de
   * llave. Se manda solo esto (no el form completo) porque el endpoint de
   * validación no persiste nada y no necesita datos de identidad.
   */
  private datosDeRuteo(): Record<string, any> {
    const keys = [
      'bank_code',
      'account_type_bank',
      'pix_key',
      'clabe',
      'iban',
      'cvu',
      'alias',
    ];
    const out: Record<string, any> = {};
    for (const k of keys) {
      const v = this.formValues[k];
      if (v != null && String(v).trim() !== '') out[k] = v;
    }
    return out;
  }

  /**
   * El valor a validar suele estar en `account_bank` (la universal de Vita)
   * o sus variantes.
   */
  private resolveValidateValue(_type: string): string | undefined {
    const fv = this.formValues;
    const v = fv['account_bank'] ?? fv['pix_key'] ?? fv['clabe'] ?? fv['iban'] ?? fv['cvu'] ?? fv['alias'];
    if (v == null || v === '') return undefined;
    // Los espacios y guiones que la gente copia del certificado del banco
    // hacían fallar la validación por formato. Solo se normaliza lo que se
    // manda a validar; lo guardado no se toca acá.
    const limpio = String(v).replace(/[\s-]/g, '');
    return limpio !== '' ? limpio : undefined;
  }

  /**
   * Resuelve el nombre visible del banco para la card de la libreta:
   *  1) Label de la opción seleccionada en el select `bank_code`.
   *  2) Valor literal de `bank_name` (si el schema lo trae como input).
   *  3) Fallback: label humano de la variante ("Colombia · BREB").
   */
  private resolveBancoNombre(): string {
    const fields = this.data.schema?.fields || [];
    const bankField = fields.find(
      (f) => f.key === 'bank_code' && f.type === 'select',
    );
    if (bankField?.options && this.formValues['bank_code'] !== undefined) {
      const selected = String(this.formValues['bank_code']);
      const opt = bankField.options.find((o) => String(o.value) === selected);
      if (opt?.label) return opt.label;
    }
    if (this.formValues['bank_name']) {
      return String(this.formValues['bank_name']);
    }
    return this.data.variantLabel;
  }

  /**
   * Resuelve la máscara de cuenta ("●●●●1234") tomando el identificador
   * universal de routing. Para CO/VE/CL/BR suele ser `account_bank`; en
   * VE pago móvil, `associate_phone`. Si nada matchea, cadena vacía.
   */
  private resolveCuentaMask(): string {
    const candidates = [
      'account_bank',
      'associate_phone',
      'account_number',
      'clabe',
      'iban',
      'pix_key',
    ];
    for (const key of candidates) {
      const v = this.formValues[key];
      if (v === undefined || v === null || v === '') continue;
      const s = String(v);
      if (s.length > 4) return '●●●●' + s.slice(-4);
      return '●●' + s;
    }
    return '';
  }
}
